// /composables/useEvents.ts
import {
  fetchAccessibleEventIds,
  fetchEventsByIdsInBatches,
  sortEventsByUrgency,
} from '~/services/events'
import type { TypeEventWithoutUnresolvableLinksResponse } from '~/types/contentful'
import { unref, computed, ref, watch, type Ref } from 'vue'
import { clearNuxtData } from '#app'

type MaybeRef<T> = T | Ref<T>

type UseEventsParams = {
  searchQuery?: MaybeRef<string | null | undefined>
  brandId?: MaybeRef<string | null | undefined>
  productId?: MaybeRef<string | null | undefined>
  pageSize?: number
}

type BundlePayload = {
  entryIds: string[]
  brands: { label: string; value: string }[]
  products: { label: string; value: string }[]
  list: TypeEventWithoutUnresolvableLinksResponse[]
  fetchedAt: number
}

export function useEvents(params: UseEventsParams = {}) {
  const { $client, $userAccessCheck, $visibility } = useNuxtApp()
  const { locale } = useI18n()
  const { highestUserLevel } = storeToRefs(useUserStore())

  // unwrap inputs
  const q = computed(() => String(unref(params.searchQuery) ?? '').trim())
  const b = computed(() => String(unref(params.brandId) ?? '').trim())
  const p = computed(() => String(unref(params.productId) ?? '').trim())

  // paging
  const pageSize = params.pageSize ?? 6
  const currentPage = ref(1)

  const asyncDataKey = `events:bundle:${q.value}:${b.value}:${p.value}:${locale.value}`

  // One asyncData to rule them all (fixes SSR empties)
  const {
    data: bundle,
    status,
    refresh,
  } = useAsyncData<BundlePayload>(
    asyncDataKey,
    async () => {
      const search = q.value.length >= 2 ? q.value : undefined

      const idsRes = await fetchAccessibleEventIds({
        $client,
        $userAccessCheck,
        $visibility,
        locale: locale.value,
        highestUserLevel: highestUserLevel.value,
        searchQuery: search,
        brandId: b.value || undefined,
        productId: p.value || undefined,
      })

      const entryIds = idsRes.entryIds ?? []
      const items = entryIds.length
        ? await fetchEventsByIdsInBatches({
            $client,
            locale: locale.value,
            ids: entryIds,
          })
        : []

      const list = sortEventsByUrgency(items)

      return {
        entryIds,
        brands: idsRes.brands ?? [],
        products: idsRes.products ?? [],
        list,
        fetchedAt: Date.now(),
      }
    },
    {
      // SSR gets a filled object; CSR reuses then refreshes
      default: () => ({
        entryIds: [],
        brands: [],
        products: [],
        list: [],
        fetchedAt: Date.now(),
      }),
      server: true,
      lazy: false,
    },
  )

  // Refresh when inputs change (client-side).
  // On the first trigger (initial URL hydration), clearNuxtData is needed to
  // invalidate the cached SSR payload so refresh() truly re-executes.
  // On subsequent triggers (user interactions), we skip clearing so the current
  // brands/products stay visible in the selectors while the new data loads.
  let isFirstChange = true
  watch([q, b, p, locale], () => {
    currentPage.value = 1
    if (isFirstChange) {
      clearNuxtData(asyncDataKey)
      isFirstChange = false
    }
    refresh()
  })

  // Derive view state
  const allEvents = computed(() => bundle.value?.list ?? [])
  const pagedEvents = computed(() =>
    allEvents.value.slice(0, pageSize * currentPage.value),
  )
  const brands = computed(() => bundle.value?.brands ?? [])
  const products = computed(() => bundle.value?.products ?? [])

  const loadMore = () => {
    currentPage.value++
  }

  return {
    // statuses (single source)
    idsStatus: status,
    eventsStatus: status,

    // data
    allEvents,
    pagedEvents,
    brands,
    products,

    // controls
    currentPage,
    loadMore,
    refresh,
  }
}
